import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  Radio,
  RadioGroup,
  Alert,
  InputAdornment,
  Tooltip,
  Snackbar,
} from '@mui/material';
import { Iconify } from '../../components/iconify';
import { getPromotionStockByType } from './promotionStockHelpers';
import { fromApiPromotionPricingType } from './promotionPricingHelpers';

const selectMenuProps = {
  disableScrollLock: true,
  PaperProps: {
    sx: {
      maxHeight: 300,
      overflow: 'auto',
      zIndex: 1400,
      '& .MuiList-root': {
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
  },
};

export default function MultiLayerOffer({
  formData,
  handleInputChange,
  handleOpenProductModal,
  handleRemoveProduct,
  handleProductCompulsoryChange,
  handleProductQuantityChange,
  handleProductTypeChange,
  handleProductPricingChange,
  handleSlabProductQuantityChange,
  handleSlabProductPricingChange,
  multiLayerStep,
  setMultiLayerStep,
  setModalType,
  setCurrentSlabIndex,
  setModalTitle,
  setShowProductModal,
  handleSlabRemoveProduct,
  handleSlabProductTypeChange,
  handleSlabProductCompulsoryChange,
  buyProductsSearch,
  setBuyProductsSearch,
  errors = {},
  showAllSteps = false,
  readOnly = false,
}) {
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const buyPricingType = fromApiPromotionPricingType(formData.buyPricingType, 'sales');
  const buildMenuProps = (custom = {}) => ({
    ...selectMenuProps,
    ...custom,
    PaperProps: {
      ...(selectMenuProps.PaperProps || {}),
      ...(custom.PaperProps || {}),
      sx: {
        ...(selectMenuProps.PaperProps?.sx || {}),
        ...(custom.PaperProps?.sx || {}),
      },
    },
  });

  const limitDecimals = (val, max = 2) => {
    if (val === '' || val === null || val === undefined) return '';
    const s = String(val).replace(/[^\d.]/g, '');
    const parts = s.split('.');
    const intPart = parts[0] || '0';
    if (parts.length === 1) return intPart;
    const frac = (parts[1] || '').slice(0, max);
    return frac.length ? `${intPart}.${frac}` : intPart;
  };
  const limitOptionsForBuyType = (options) => {
    const buyProductType = formData.buyProductType || 'box-only';
    if (buyProductType === 'box-only') {
      return options.filter((option) => option.value === 'Box');
    }
    if (buyProductType === 'piece-only') {
      return options.filter((option) => option.value === 'Piece');
    }
    return options;
  };

  const getProductTypeOptions = (split, isForBuyProduct = true) => {
    if (split === 0) return [{ value: 'Box', label: 'Box' }];
    if (split === 1) return [{ value: 'Piece', label: 'Piece' }];
    if (split === 2) {
      if (isForBuyProduct) {
        return limitOptionsForBuyType([
          { value: 'Box', label: 'Box' },
          { value: 'Piece', label: 'Piece' },
          { value: 'Both', label: 'Both' },
        ]);
      } else {
        return [
          { value: 'Box', label: 'Box' },
          { value: 'Piece', label: 'Piece' },
        ];
      }
    }
    const options = [{ value: 'Box', label: 'Box' }];
    return isForBuyProduct ? limitOptionsForBuyType(options) : options;
  };

  const getDefaultProductType = (split) => {
    if (split === 0) return 'Box';
    if (split === 1) return 'Piece';
    return 'Box';
  };

  const normalizeProductType = (type, split) => {
    if (type === 'Both' || !type) {
      return getDefaultProductType(split);
    }
    return type;
  };

  const getDefaultTypeBasedOnBuyProductType = (product) => {
    const buyProductType = formData.buyProductType || 'box-only';
    if (buyProductType === 'box-only') return 'Box';
    if (buyProductType === 'piece-only') return 'Piece';
    return product.type || getDefaultProductType(product.split);
  };

  const toNumber = (value) => {
    if (value === undefined || value === null || value === '') return 0;
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const pickPositive = (...vals) => {
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };

  const getMultiLayerBuyPricingMode = (product, priceKind = 'Box', defaultPricingType = 'sales') => {
    if (priceKind === 'Piece') {
      const pieceMode = product.piecePricingType ?? product.piece_pricing_type;
      if (pieceMode && pieceMode !== '' && pieceMode !== 'undefined' && pieceMode !== 'null') {
        return pieceMode === 'offer' ? 'offer' : 'sales';
      }
    } else {
      const boxMode = product.boxPricingType ?? product.box_pricing_type;
      if (boxMode && boxMode !== '' && boxMode !== 'undefined' && boxMode !== 'null') {
        return boxMode === 'offer' ? 'offer' : 'sales';
      }
    }
    const fallback = product.pricingType ?? product.pricing_type;
    if (fallback && fallback !== '' && fallback !== 'undefined' && fallback !== 'null') {
      return fallback === 'offer' ? 'offer' : 'sales';
    }
    const resolvedDefault =
      defaultPricingType &&
        defaultPricingType !== '' &&
        defaultPricingType !== 'undefined' &&
        defaultPricingType !== 'null'
        ? defaultPricingType
        : 'sales';
    return resolvedDefault === 'offer' ? 'offer' : 'sales';
  };

  const getMultiLayerGetPricingMode = (product, priceKind = 'Box', defaultPricingType = 'sales') =>
    getMultiLayerBuyPricingMode(product, priceKind, defaultPricingType);

  const getForcedPrice = (product, type = 'Box', mode = 'sales') => {
    if (type === 'Piece') {
      if (mode === 'offer') {
        return toNumber(pickPositive(product.pieceOfferPrice, product.offerPiecePrice, product.offerPrice, 0));
      }
      return toNumber(
        pickPositive(
          product.pieceSalesPrice,
          product.PiecePrice,
          product.piece_sales_price,
          product.salesPrice,
          product.price,
          0
        )
      );
    }
    if (mode === 'offer') {
      return toNumber(pickPositive(product.boxOfferPrice, product.offerBoxPrice, product.offerPrice, 0));
    }
    return toNumber(pickPositive(product.boxSalesPrice, product.salesPrice, product.box_sales_price, product.price, 0));
  };

  const getDefaultBuyPricingType = () => buyPricingType;

  const getStockByType = (product) => {
    if (!product) return 0;

    const currentType = product.type || normalizeProductType(product.type, product.split);
    const selectedType = currentType === 'Piece' || currentType === 'piece' ? 'Piece' :
      currentType === 'Box' || currentType === 'box' ? 'Box' :
        normalizeProductType(product.type, product.split);
    return getPromotionStockByType(product, selectedType);
  };

  const normalizeProductPricing = (product) => {
    const boxSales = product.boxSalesPrice ?? product.box_price ?? product.salesPrice ?? product.price;
    const boxOffer = product.boxOfferPrice ?? product.box_offer_price ?? product.offerBoxPrice ?? product.offerPrice;
    const pieceSales = product.pieceSalesPrice ?? product.piece_price ?? product.piece_sales_price ?? product.PiecePrice ?? product.price;
    const pieceOffer = product.pieceOfferPrice ?? product.piece_offer_price ?? product.offerPiecePrice ?? product.offerPrice;
    return {
      ...product,
      boxSalesPrice: toNumber(boxSales),
      boxOfferPrice: toNumber(boxOffer),
      pieceSalesPrice: toNumber(pieceSales),
      pieceOfferPrice: toNumber(pieceOffer),
    };
  };

  const getFirstBuyProduct = () => {
    const list = formData.buyProducts || [];
    if (!Array.isArray(list) || list.length === 0) return null;
    return normalizeProductPricing(list[0]);
  };

  const getMinBuyProductPrice = (priceKind = 'Box') => {
    const list = formData.buyProducts || [];
    if (!Array.isArray(list) || list.length === 0) return 0;
    const prices = list
      .map((raw) => {
        const p = normalizeProductPricing(raw);
        const mode = getMultiLayerBuyPricingMode(p, priceKind, getDefaultBuyPricingType());
        return getForcedPrice(p, priceKind, mode);
      })
      .filter((price) => Number.isFinite(price) && price > 0);
    if (prices.length === 0) return 0;
    return Math.min(...prices);
  };

  const getBaseBoxPrice = () => getMinBuyProductPrice('Box');

  const getBasePiecePrice = () => getMinBuyProductPrice('Piece');

  const computeFinalBoxPrice = (slab) => {
    const base = getBaseBoxPrice();
    const val = parseFloat(slab.boxProductsValue || 0) || 0;
    if (slab.discountType === 'percentage') {
      return Math.max(base - (base * val) / 100, 0);
    }
    if (slab.discountType === 'fixed') {
      return Math.max(base - val, 0);
    }
    return base;
  };

  const computeFinalPiecePrice = (slab) => {
    const base = getBasePiecePrice();
    const val = parseFloat(slab.pieceProductsValue || 0) || 0;
    if (slab.discountType === 'percentage') {
      return Math.max(base - (base * val) / 100, 0);
    }
    if (slab.discountType === 'fixed') {
      return Math.max(base - val, 0);
    }
    return base;
  };

  const filteredBuyProducts = (formData.buyProducts || [])
    .map((product, index) => {
      const normalized = normalizeProductPricing(product);
      const resolvedType = (() => {
        const buyType = formData.buyProductType || 'box-only';
        if (buyType === 'box-only') return 'Box';
        if (buyType === 'piece-only') return 'Piece';
        const hasValidType =
          normalized.type &&
          normalized.type !== '' &&
          normalized.type !== 'undefined' &&
          normalized.type !== 'null';
        return hasValidType ? normalized.type : getDefaultProductType(normalized.split);
      })();
      return {
        ...normalized,
        type: resolvedType,
        originalIndex: product.originalIndex !== undefined ? product.originalIndex : index
      };
    })
    .filter((product) => {
      const searchLower = (buyProductsSearch || '').toLowerCase();
      return (
        product.name.toLowerCase().includes(searchLower) ||
        (product.code && product.code.toLowerCase().includes(searchLower)) ||
        product.id.toString().toLowerCase().includes(searchLower)
      );
    });

  const handleAddSlab = () => {
    const slabs = formData.slabs || [];
    const newSlab = {
      id: Date.now(),
      slabOrder: slabs.length + 1,
      slabName: `Slab ${slabs.length + 1}`,
      productType: 'box-only',
      boxMinQuantity: '',
      boxMaxQuantity: '',
      pieceMinQuantity: '',
      pieceMaxQuantity: '',
      discountType: '',
      boxProductsValue: '',
      pieceProductsValue: '',
      hasGetProducts: false,
      getQuantity: '',
      getProducts: [],
    };
    handleInputChange('slabs', [...slabs, newSlab]);
  };

  const handleDeleteSlab = (slabIndex) => {
    const slabs = [...(formData.slabs || [])];
    slabs.splice(slabIndex, 1);
    handleInputChange('slabs', slabs);
  };

  const getPackSize = () => {
    const firstProduct = getFirstBuyProduct();
    if (!firstProduct) return 0;
    return parseInt(
      firstProduct.pack_size ||
      firstProduct.pack ||
      firstProduct.unit_per_box ||
      firstProduct.unitPerBox ||
      firstProduct.size ||
      0,
      10
    );
  };

  const validateQuantityRange = (value, fieldName) => {
    if (value === '' || value === null || value === undefined) {
      return { isValid: true, value: '' };
    }

    const numValue = parseInt(value, 10);

    if (isNaN(numValue)) {
      return { isValid: false, value: value };
    }

    if (numValue < 0) {
      setSnackbar({
        open: true,
        message: `${fieldName} must be between 0 and 999. Value cannot be negative.`
      });
      return { isValid: false, value: '0' };
    }

    if (numValue > 999) {
      setSnackbar({
        open: true,
        message: `${fieldName} must be between 0 and 999. Maximum value is 999.`
      });
      return { isValid: false, value: '999' };
    }

    return { isValid: true, value: value };
  };

  const handleQuantityKeyDown = (e, currentValue) => {
    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
      e.preventDefault();
      return;
    }

    if (e.key >= '0' && e.key <= '9') {
      const selectionStart = e.target.selectionStart || 0;
      const selectionEnd = e.target.selectionEnd || 0;
      const newValue = currentValue.slice(0, selectionStart) + e.key + currentValue.slice(selectionEnd);
      const numValue = parseInt(newValue, 10);

      if (!isNaN(numValue) && numValue > 999) {
        e.preventDefault();
        setSnackbar({
          open: true,
          message: 'Value must be between 0 and 999. Maximum value is 999.'
        });
      }
    }
  };

  const handleQuantityChange = (slabIndex, field, value, fieldName) => {
    const validation = validateQuantityRange(value, fieldName);

    if (!validation.isValid && value !== '') {
      handleSlabChange(slabIndex, field, validation.value);
      return;
    }

    handleSlabChange(slabIndex, field, value);
  };

  const handleSlabChange = (slabIndex, field, value) => {
    const slabs = [...(formData.slabs || [])];
    const updatedSlab = { ...slabs[slabIndex] };

    if (field === 'boxQuantity') {
      updatedSlab.boxMinQuantity = value;
      updatedSlab.boxMaxQuantity = value;
    } else if (field === 'pieceQuantity') {
      let pieceValue = value;
      if (formData.buyProductType === 'both') {
        const packSize = getPackSize();
        const boxMax = parseInt(updatedSlab.boxMaxQuantity, 10);
        if (packSize > 0 && !isNaN(boxMax) && pieceValue !== '') {
          const maxAllowed = (boxMax + 1) * packSize - 1;
          const numValue = parseInt(pieceValue, 10);
          if (!isNaN(numValue) && numValue > maxAllowed) {
            pieceValue = maxAllowed.toString();
            setSnackbar({
              open: true,
              message: `Piece Quantity cannot exceed ${maxAllowed}`,
            });
          }
        }
      }
      updatedSlab.pieceMinQuantity = pieceValue;
      updatedSlab.pieceMaxQuantity = pieceValue;
    } else {
      updatedSlab[field] = value;
    }

    if (formData.buyProductType === 'both' && (field === 'boxMaxQuantity' || field === 'boxQuantity')) {
      const packSize = getPackSize();
      if (packSize > 0) {
        const boxMax = parseInt(value) || 0;
        const calculatedPieceMax = (boxMax + 1) * packSize - 1;
        const currentPiece = parseInt(updatedSlab.pieceMaxQuantity) || 0;

        if (currentPiece > calculatedPieceMax) {
          updatedSlab.pieceMinQuantity = calculatedPieceMax.toString();
          updatedSlab.pieceMaxQuantity = calculatedPieceMax.toString();
        } else if (!updatedSlab.pieceMaxQuantity) {
          updatedSlab.pieceMinQuantity = updatedSlab.pieceMinQuantity || '0';
          updatedSlab.pieceMaxQuantity = updatedSlab.pieceMaxQuantity || '0';
        }
      }
    }

    slabs[slabIndex] = updatedSlab;
    handleInputChange('slabs', slabs);
  };

  const handleAddGetProducts = (slabIndex) => {
    setCurrentSlabIndex(slabIndex);
    setModalType('getForSlab');
    setModalTitle(`Add Get Products for Slab ${slabIndex + 1}`);
    setShowProductModal(true);
  };

  const validateSlabRanges = () => {
    return { hasOverlap: false };
    /*
    const slabs = formData.slabs || [];
    const boxRanges = [];
    const pieceRanges = [];

    slabs.forEach((slab, index) => {
      const boxMin = parseInt(slab.boxMinQuantity) || 0;
      const boxMax = parseInt(slab.boxMaxQuantity) || 0;
      if (boxMin > 0 || boxMax > 0) {
        boxRanges.push({ slabIndex: index, min: boxMin, max: boxMax });
      }

      const pieceMin = parseInt(slab.pieceMinQuantity) || 0;
      const pieceMax = parseInt(slab.pieceMaxQuantity) || 0;
      if (pieceMin > 0 || pieceMax > 0) {
        pieceRanges.push({ slabIndex: index, min: pieceMin, max: pieceMax });
      }
    });

    for (let i = 0; i < boxRanges.length; i++) {
      for (let j = i + 1; j < boxRanges.length; j++) {
        const range1 = boxRanges[i];
        const range2 = boxRanges[j];
        if (
          (range1.min <= range2.max && range1.max >= range2.min) ||
          (range2.min <= range1.max && range2.max >= range1.min)
        ) {
          return {
            hasOverlap: true,
            message: `Box quantity overlap detected between Slab ${range1.slabIndex + 1} (${range1.min}-${range1.max}) and Slab ${range2.slabIndex + 1} (${range2.min}-${range2.max})`,
          };
        }
      }
    }

    for (let i = 0; i < pieceRanges.length; i++) {
      for (let j = i + 1; j < pieceRanges.length; j++) {
        const range1 = pieceRanges[i];
        const range2 = pieceRanges[j];
        if (
          (range1.min <= range2.max && range1.max >= range2.min) ||
          (range2.min <= range1.max && range2.max >= range1.min)
        ) {
          return {
            hasOverlap: true,
            message: `Piece quantity overlap detected between Slab ${range1.slabIndex + 1} (${range1.min}-${range1.max}) and Slab ${range2.slabIndex + 1} (${range2.min}-${range2.max})`,
          };
        }
      }
    }

    return { hasOverlap: false };
    */
  };

  const overlapCheck = validateSlabRanges();

  const getSortedSlabs = () => {
    const slabs = formData.slabs || [];
    return [...slabs].sort((a, b) => {
      const aBoxMin = parseInt(a.boxMinQuantity) || Infinity;
      const bBoxMin = parseInt(b.boxMinQuantity) || Infinity;
      const aPieceMin = parseInt(a.pieceMinQuantity) || Infinity;
      const bPieceMin = parseInt(b.pieceMinQuantity) || Infinity;

      const aMin = Math.min(aBoxMin, aPieceMin);
      const bMin = Math.min(bBoxMin, bPieceMin);

      return aMin - bMin;
    });
  };

  const getErrorSummary = () => {
    const errorMessages = [];

    Object.keys(errors).forEach(key => {
      const errorValue = errors[key];
      if (errorValue && typeof errorValue === 'string') {
        let fieldName = key;

        if (key === 'buyProducts') {
          fieldName = '📦 Buy Products';
        } else if (key === 'slabs') {
          fieldName = '📊 Slabs Configuration';
        } else if (key === 'slabOverlap') {
          fieldName = '⚠️ Slab Range Overlap';
        } else if (key === 'compulsoryQuantity') {
          fieldName = '✓ Compulsory Quantity';
        } else if (key.startsWith('buyProduct_')) {
          const match = key.match(/buyProduct_(\d+)_(.+)/);
          if (match) {
            const index = parseInt(match[1]) + 1;
            const field = match[2].replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
            const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
            fieldName = `📦 Buy Product #${index} → ${capitalizedField}`;
          }
        } else if (key.startsWith('slab_')) {
          const slabMatch = key.match(/slab_(\d+)_getProduct_(\d+)_(.+)/);
          if (slabMatch) {
            const slabIndex = parseInt(slabMatch[1]) + 1;
            const productIndex = parseInt(slabMatch[2]) + 1;
            const field = slabMatch[3].replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
            const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
            fieldName = `📊 Slab ${slabIndex} → Get Product #${productIndex} → ${capitalizedField}`;
          } else {
            const match = key.match(/slab_(\d+)_(.+)/);
            if (match) {
              const index = parseInt(match[1]) + 1;
              const field = match[2].replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
              const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
              fieldName = `📊 Slab ${index} → ${capitalizedField}`;
            }
          }
        }

        errorMessages.push({ field: fieldName, message: errorValue, key });
      }
    });

    return errorMessages;
  };

  const errorSummary = getErrorSummary();

  const hasPackSizePriceErrors = (formData.buyProducts || []).some((product, index) => {
    if (index === 0) return false;
    const originalIndex = product.originalIndex !== undefined ? product.originalIndex : index;
    return errors[`buyProduct_${originalIndex}_packSize`] ||
      errors[`buyProduct_${originalIndex}_boxPrice`] ||
      errors[`buyProduct_${originalIndex}_piecePrice`];
  });

  const showStep1 = showAllSteps || multiLayerStep === 1;
  const showStep2 = showAllSteps || multiLayerStep === 2;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#212B36',
            }}
          >
            Multi-Layer Special Offer Configuration
          </Typography>
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.6rem' }}>
                  • Configure slabs with quantity thresholds and discount rules.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.6rem' }}>
                  • Assign shared buy products and optional get products per slab.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: '0.6rem' }}>
                  • Ensure slab ranges do not overlap to keep eligibility clear.
                </Typography>
              </Box>
            }
            arrow
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1e3a5f',
                  color: 'white',
                  fontSize: '0.465rem',
                  maxWidth: 500,
                  boxShadow: 2,
                  '& .MuiTooltip-arrow': {
                    color: '#1e3a5f',
                  },
                },
              },
            }}
          >
            <IconButton size="small" sx={{ color: '#003DA5', mt: -0.5 }}>
              <Iconify icon="eva:info-fill" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: '#212b36',
            fontStyle: 'italic'
          }}
        >
          Setup multiple slab-based offers with escalating discounts for same products
        </Typography>
      </Box>



      {showStep1 && (
        <Box>
          <Card
            sx={{
              bgcolor: 'white',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
              borderRadius: 2,
              border: '1px solid #E5E8EB'
            }}
          >
            <CardContent sx={{ px: 3, py: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#212B36',

                }}
              >
                Configure Buy Products
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#212b36',
                  mb: 2
                }}
              >
                Select products that will be used across all slabs
              </Typography>

              {/* <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Radio className="checkBox" />}
                  label="Invoice Wise"
                  value="invoice-wise"
                  checked={formData.invoiceWise === 'invoice-wise'}
                  onChange={(e) => {
                    handleInputChange('invoiceWise', e.target.value);
                  }}
                />
                <FormControlLabel
                  control={<Radio className="checkBox" />}
                  label="Product Wise"
                  value="product-wise"
                  checked={formData.invoiceWise === 'product-wise'}
                  onChange={(e) => {
                    handleInputChange('invoiceWise', e.target.value);
                  }}
                />
              </Box> */}

              {formData.invoiceWise === 'invoice-wise' && (
                <Box sx={{ mb: 3, maxWidth: 640 }}>
                  <Box sx={{ mb: 1 }}>
                    <TextField
                      className="form-input-border"
                      fullWidth
                      size="small"
                      label="Amount"
                      placeholder="Amount Value"
                      sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth error={!!errors.invoiceDiscountType}>
                      <InputLabel sx={{ fontSize: '14px' }}>Discount Type</InputLabel>
                      <Select
                        className="input-border"
                        value={formData.invoiceDiscountType || ''}
                        label="Discount Type"
                        onChange={(e) => handleInputChange('invoiceDiscountType', e.target.value)}
                        disabled={readOnly}
                        sx={{
                          height: '35px',
                          '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: '35px !important',
                          },
                        }}
                      >
                        <MenuItem value="fixed" sx={{ fontSize: '14px' }}>
                          Fixed Amount
                        </MenuItem>
                        <MenuItem value="percentage" sx={{ fontSize: '14px' }}>
                          Percentage
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <TextField
                    className="form-input-border"
                    fullWidth
                    size="small"
                    type="number"
                    label="Discount Value"
                    placeholder="Discount Value"
                    value={formData.invoiceDiscountValue || ''}
                    onChange={(e) => handleInputChange('invoiceDiscountValue', e.target.value)}
                    error={!!errors.invoiceDiscountValue}
                    helperText={errors.invoiceDiscountValue || ''}
                    disabled={readOnly}
                    inputProps={{
                      min: 0,
                      max: formData.invoiceDiscountType === 'percentage' ? 100 : 999999,
                      step: formData.invoiceDiscountType === 'percentage' ? 1 : 0.01,
                    }}
                  />
                </Box>
              )}

              <Box sx={{ mb: 2, display: formData.invoiceWise === 'invoice-wise' ? 'none' : 'block' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#212B36'
                    }}
                  >
                    Product Type
                  </Typography>
                  {(formData.buyProducts || []).length > 0 && (
                    <Tooltip
                      title="Product Type cannot be changed after adding products. Remove all products to change."
                      arrow
                      placement="right"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Iconify
                          icon="eva:lock-fill"
                          sx={{
                            fontSize: 18,
                            color: '#FF9800',
                            cursor: 'help'
                          }}
                        />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
                {/* <Tooltip 
                  title={(formData.buyProducts || []).length > 0 ? "Remove all products to change product type." : ""}
                  arrow
                  placement="top"
                > */}
                <RadioGroup
                  row
                  value={formData.buyProductType || 'box-only'}
                  onChange={(e) => {
                    if ((formData.buyProducts || []).length > 0 || readOnly) {
                      return;
                    }
                    handleInputChange('buyProductType', e.target.value);
                  }}
                  sx={{ gap: 3 }}
                >
                  <FormControlLabel
                    value="box-only"
                    control={
                      <Radio
                        disabled={(formData.buyProducts || []).length > 0 || readOnly}
                        className="checkBox"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: (formData.buyProducts || []).length > 0 ? '#919EAB' : '#212B36'
                        }}
                      >
                        Box Only
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="piece-only"
                    control={
                      <Radio
                        disabled={(formData.buyProducts || []).length > 0 || readOnly}
                        className="checkBox"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: (formData.buyProducts || []).length > 0 ? '#919EAB' : '#212B36'
                        }}
                      >
                        Piece Only
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="both"
                    control={
                      <Radio
                        disabled={(formData.buyProducts || []).length > 0 || readOnly}
                        className="checkBox"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: (formData.buyProducts || []).length > 0 ? '#919EAB' : '#212B36'
                        }}
                      >
                        Both
                      </Typography>
                    }
                  />
                </RadioGroup>
                {/* </Tooltip> */}
                {/* {(formData.buyProducts || []).length > 0 && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mt: 1.5,
                      py: 0.5,
                      '& .MuiAlert-icon': {
                        fontSize: 18
                      }
                    }}
                  >
                    {/* <Typography variant="caption" sx={{ fontSize: '12px' }}>
                      Remove all products to change product type.
                    </Typography> */}
                {/* </Alert> */}
                {/* )}  */}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  mb: 1,
                  gap: 2,
                  display: formData.invoiceWise === 'invoice-wise' ? 'none' : 'flex',
                }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: '#212B36'
                    }}
                  >
                    {readOnly
                      ? `Selected Products (${(formData.buyProducts || []).length})`
                      : `Selected Products (${(formData.buyProducts || []).length}/200)`}
                  </Typography>
                  {errors.buyProducts && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#FF5630',
                        display: 'block',
                        mt: 0.5
                      }}
                    >
                      {errors.buyProducts}
                    </Typography>
                  )}
                  {!readOnly &&
                    !errors.buyProducts &&
                    (formData.buyProducts || []).length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#212b36',
                          display: 'block',

                          fontSize: '12px'
                        }}
                      >
                        Product details: Code, Name, Price, Stock
                      </Typography>
                    )}
                  {(formData.buyProducts || []).length >= 200 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#FF9800',
                        display: 'block',
                        mt: 0.5,
                        fontSize: '12px',
                        fontWeight: 600
                      }}
                    >
                      Maximum limit reached (200 products)
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <FormControl size="small" sx={{ minWidth: 165 }}>
                    <InputLabel sx={{ fontSize: '13px' }} className="commonColor">Pricing</InputLabel>
                    <Select
                      className="input-border"
                      value={buyPricingType}
                      label="Pricing"
                      onChange={(e) => {
                        const value = e.target.value === 'offer' ? 'offer' : 'sales';
                        handleInputChange('buyPricingType', value);
                      }}
                      disabled={readOnly}
                      sx={{ fontSize: '13px' }}
                      MenuProps={buildMenuProps({
                        PaperProps: {
                          sx: {
                            '& .MuiMenuItem-root': {
                              fontSize: '13px',
                            }
                          }
                        }
                      })}
                    >
                      <MenuItem value="sales" sx={{ fontSize: '13px' }}>
                        Standard Price
                      </MenuItem>
                      <MenuItem value="offer" sx={{ fontSize: '13px' }}>
                        Offer Price
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {!readOnly && (
                    <Button
                      className="button-variant-contained"
                      startIcon={<Iconify icon="eva:plus-fill" width={20} />}
                      onClick={() => handleOpenProductModal('buy')}
                      disabled={(formData.buyProducts || []).length >= 200}
                      disableRipple
                    >
                      Add More Products
                    </Button>
                  )}
                </Box>

              </Box>
              {/* 
              {hasPackSizePriceErrors && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 1,
                    '& .MuiAlert-icon': {
                      color: '#FF5630'
                    }
                  }}
                >
                 
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    Selected product must match the first product's pack size and price. Please check the individual product cards below for details.
                  </Typography>
                </Alert>
              )} */}

              {formData.invoiceWise === 'invoice-wise' ? null : ((formData.buyProducts || []).length === 0 ? (
                <Box
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    bgcolor: '#F9FAFB',
                    border: '2px dashed rgba(59, 59, 59, 0.45)',
                    borderRadius: 2,
                  }}
                >
                  <Iconify
                    icon="eva:cube-outline"
                    sx={{ fontSize: 48, color: '#919EAB', mb: 2 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1,
                      color: '#212b36',
                      fontWeight: 600
                    }}
                  >
                    No Products Selected
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#212b36'
                    }}
                  >
                    {readOnly
                      ? 'No products are associated with this promotion.'
                      : 'Click "Add More Products" to start building your multi-layer offer'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <Box sx={{ width: '100%', maxWidth: 900 }}>
                    <Box sx={{ mt: 1, maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                      <Box
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          bgcolor: 'background.paper',
                          pb: 1,
                          mb: 1,
                          pt: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <TextField
                          className="allow-interaction input-border"
                          fullWidth
                          placeholder="Search by product name or product code"
                          value={buyProductsSearch}
                          onChange={(e) => setBuyProductsSearch(e.target.value)}
                          InputProps={{
                            className: 'allow-interaction',
                            startAdornment: (
                              <InputAdornment position="start">
                                <Iconify icon="eva:search-fill" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {filteredBuyProducts.map((product) => (
                          <Card
                            key={`${product.id}-${product.originalIndex}`}
                            sx={{
                              border: '1px solid rgba(59, 59, 59, 0.45)',
                              boxShadow: 'none',
                              borderRadius: 1.5,
                              position: 'relative',
                              '&:hover': {
                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                              }
                            }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 600,
                                      color: '#212B36',
                                      mb: 0.5,
                                    }}
                                  >
                                    {product.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#212b36',
                                      mb: 1,
                                      fontSize: '13px'
                                    }}
                                  >
                                    Code: {product.code || product.id} | Stock: {getStockByType(product)}
                                  </Typography>

                                  {errors[`buyProduct_${product.originalIndex}_packSize`] && (
                                    <Alert
                                      severity="error"
                                      sx={{
                                        mb: 2,
                                        borderRadius: 1,
                                        '& .MuiAlert-icon': {
                                          color: '#FF5630'
                                        }
                                      }}
                                    >
                                      {errors[`buyProduct_${product.originalIndex}_packSize`]}
                                    </Alert>
                                  )}

                                  {errors[`buyProduct_${product.originalIndex}_boxPrice`] && !errors[`buyProduct_${product.originalIndex}_packSize`] && (
                                    <Alert
                                      severity="error"
                                      sx={{
                                        mb: 2,
                                        borderRadius: 1,
                                        '& .MuiAlert-icon': {
                                          color: '#FF5630'
                                        }
                                      }}
                                    >
                                      {errors[`buyProduct_${product.originalIndex}_boxPrice`]}
                                    </Alert>
                                  )}

                                  {errors[`buyProduct_${product.originalIndex}_piecePrice`] && !errors[`buyProduct_${product.originalIndex}_packSize`] && !errors[`buyProduct_${product.originalIndex}_boxPrice`] && (
                                    <Alert
                                      severity="error"
                                      sx={{
                                        mb: 2,
                                        borderRadius: 1,
                                        '& .MuiAlert-icon': {
                                          color: '#FF5630'
                                        }
                                      }}
                                    >
                                      {errors[`buyProduct_${product.originalIndex}_piecePrice`]}
                                    </Alert>
                                  )}

                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={product.compulsory ? (product.type === 'Both' ? 2.4 : 3) : (product.type === 'Both' ? 3 : 4)}>
                                      {(() => {
                                        const buyProductType = formData.buyProductType || 'box-only';
                                        const productSplit = product.split || 2;
                                        const canBeBoth = productSplit === 2;
                                        const isBothType = product.type === 'Both';
                                        const isBothBuyType = buyProductType === 'both';
                                        const shouldLockToBoth = isBothBuyType && canBeBoth && isBothType;
                                        const isTypeDisabled = readOnly || shouldLockToBoth;

                                        return (
                                          <FormControl fullWidth size="small" error={!!errors[`buyProduct_${product.originalIndex}_type`]}>
                                            <InputLabel sx={{ fontSize: '14px' }} className="commonColor">Product Type</InputLabel>
                                            <Select
                                              className="input-border"
                                              value={(product.type && product.type !== 'undefined' && product.type !== 'null') ? product.type : getDefaultTypeBasedOnBuyProductType(product)}
                                              label="Product Type"
                                              onChange={(e) =>
                                                handleProductTypeChange &&
                                                handleProductTypeChange(
                                                  `${product.id}-${product.originalIndex}`,
                                                  'buy',
                                                  e.target.value
                                                )
                                              }
                                              disabled={isTypeDisabled}
                                              sx={{
                                                fontSize: '14px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                  borderColor: '#000',
                                                }
                                              }}
                                              MenuProps={buildMenuProps({
                                                PaperProps: {
                                                  sx: {
                                                    '& .MuiMenuItem-root': {
                                                      fontSize: '14px',
                                                    }
                                                  }
                                                }
                                              })}
                                            >
                                              {(() => {
                                                const availableOptions = getProductTypeOptions(product.split || 2, true);
                                                if (shouldLockToBoth) {
                                                  return availableOptions.filter(opt => opt.value === 'Both').map((option) => (
                                                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '14px' }}>
                                                      {option.label}
                                                    </MenuItem>
                                                  ));
                                                }
                                                return availableOptions.map((option) => (
                                                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '14px' }}>
                                                    {option.label}
                                                  </MenuItem>
                                                ));
                                              })()}
                                            </Select>
                                            {errors[`buyProduct_${product.originalIndex}_type`] && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: '#FF5630',
                                                  display: 'block',
                                                  mt: 0.5,
                                                  ml: 1.75,
                                                  fontSize: '12px'
                                                }}
                                              >
                                                {errors[`buyProduct_${product.originalIndex}_type`]}
                                              </Typography>
                                            )}
                                          </FormControl>
                                        );
                                      })()}
                                    </Grid>

                                    {/* {(product.type === 'Box' || product.type === 'Both') && (
                                  <Grid item xs={12} sm={product.type === 'Both' ? 3 : 4}>
                                    <FormControl fullWidth size="small" error={!!errors[`buyProduct_${product.originalIndex}_boxPrice`]}>
                                      <InputLabel sx={{ fontSize: '14px' }}>Box Pricing</InputLabel>
                                      <Select
                                        className="input-border"
                                        value={product.boxPricingType || buyPricingType}
                                        label="Box Pricing"
                                        onChange={(e) => {
                                          const newSlabs = formData.buyProducts.map((p, idx) =>
                                            `${p.id}-${idx}` === `${product.id}-${product.originalIndex}`
                                              ? { ...p, boxPricingType: e.target.value }
                                              : p
                                          );
                                          handleInputChange('buyProducts', newSlabs);
                                        }}
                                        disabled={readOnly}
                                        sx={{
                                          fontSize: '14px',
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                          }
                                        }}
                                      MenuProps={buildMenuProps({
                                        PaperProps: {
                                          sx: {
                                            '& .MuiMenuItem-root': {
                                              fontSize: '14px',
                                            }
                                          }
                                        }
                                      })}
                                      >
                                        <MenuItem value="sales" sx={{ fontSize: '14px' }}>
                                          Standard
                                        </MenuItem>
                                        <MenuItem value="offer" sx={{ fontSize: '14px' }}>
                                          Offer
                                        </MenuItem>
                                      </Select>
                                      {errors[`buyProduct_${product.originalIndex}_boxPrice`] && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#FF5630',
                                            display: 'block',
                                            mt: 0.5,
                                            ml: 1.75,
                                            fontSize: '12px'
                                          }}
                                        >
                                          {errors[`buyProduct_${product.originalIndex}_boxPrice`]}
                                        </Typography>
                                      )}
                                    </FormControl>
                                  </Grid>
                                )} */}

                                    {/* {(product.type === 'Piece' || product.type === 'Both') && (
                                  <Grid item xs={12} sm={product.type === 'Both' ? 3 : 4}>
                                    <FormControl fullWidth size="small" error={!!errors[`buyProduct_${product.originalIndex}_piecePrice`]}>
                                      <InputLabel sx={{ fontSize: '14px' }}>Piece Pricing</InputLabel>
                                      <Select
                                        className="input-border"
                                        value={product.piecePricingType || buyPricingType}
                                        label="Piece Pricing"
                                        onChange={(e) => {
                                          const newSlabs = formData.buyProducts.map((p, idx) =>
                                            `${p.id}-${idx}` === `${product.id}-${product.originalIndex}`
                                              ? { ...p, piecePricingType: e.target.value }
                                              : p
                                          );
                                          handleInputChange('buyProducts', newSlabs);
                                        }}
                                        disabled={readOnly}
                                        sx={{
                                          fontSize: '14px',
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                          }
                                        }}
                                      MenuProps={buildMenuProps({
                                        PaperProps: {
                                          sx: {
                                            '& .MuiMenuItem-root': {
                                              fontSize: '14px',
                                            }
                                          }
                                        }
                                      })}
                                      >
                                        <MenuItem value="sales" sx={{ fontSize: '14px' }}>
                                          Standard
                                        </MenuItem>
                                        <MenuItem value="offer" sx={{ fontSize: '14px' }}>
                                          Offer
                                        </MenuItem>
                                      </Select>
                                      {errors[`buyProduct_${product.originalIndex}_piecePrice`] && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#FF5630',
                                            display: 'block',
                                            mt: 0.5,
                                            ml: 1.75,
                                            fontSize: '12px'
                                          }}
                                        >
                                          {errors[`buyProduct_${product.originalIndex}_piecePrice`]}
                                        </Typography>
                                      )}
                                    </FormControl>
                                  </Grid>
                                )} */}

                                    {/* Compulsory option removed for Multi-Layer buy products */}
                                  </Grid>
                                </Box>

                                {!readOnly && (

                                  <IconButton
                                    onClick={() =>
                                      handleRemoveProduct(
                                        `${product.id}-${product.originalIndex}`,
                                        'buy'
                                      )
                                    }
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      color: '#FF5630',
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 86, 48, 0.08)',
                                      }
                                    }}
                                  >
                                    <Iconify icon="eva:close-circle-fill" width={24} />
                                  </IconButton>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      )}

      {showStep2 && formData.invoiceWise !== 'invoice-wise' && (
        <Box>
          <Card
            sx={{
              bgcolor: 'white',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
              borderRadius: 2,
              border: '1px solid #E5E8EB'
            }}
          >
            <CardContent sx={{ px: 2, py: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#212B36'
                    }}
                  >
                    Slabs Configuration
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#212b36',
                      fontSize: '12px',
                      fontStyle: 'italic',

                    }}
                  >
                    Slabs are automatically sorted by quantity ranges (ascending order)
                  </Typography>
                </Box>
                
                {!readOnly && !formData.slabs || (formData.slabs && formData.slabs.length === 0) && (
                  <Button
                    className="button-variant-outlined"
                    variant="outlined"
                    startIcon={<Iconify icon="eva:plus-fill" width={20} />}
                    onClick={handleAddSlab}
                    disableRipple
                  >
                    Add New Slab
                  </Button>
                )}
              </Box>

              {overlapCheck.hasOverlap && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 1,
                    '& .MuiAlert-icon': {
                      color: '#FF5630'
                    }
                  }}
                >
                  {overlapCheck.message}
                </Alert>
              )}

              {errors.slabOverlap && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 1,
                    '& .MuiAlert-icon': {
                      color: '#FF5630'
                    }
                  }}
                >
                  {errors.slabOverlap}
                </Alert>
              )}

              {errors.slabs && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 1,
                    '& .MuiAlert-icon': {
                      color: '#FF5630'
                    }
                  }}
                >
                  {errors.slabs}
                </Alert>
              )}

              {(!formData.slabs || formData.slabs.length === 0) ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: '#F9FAFB',
                    border: '2px dashed rgba(59, 59, 59, 0.45)',
                    borderRadius: 2,
                  }}
                >
                  <Iconify
                    icon="eva:layers-outline"
                    sx={{
                      fontSize: 64,
                      color: '#212b36',
                      mb: 2
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{

                      color: '#212b36',
                      fontWeight: 600
                    }}
                  >
                    No slabs configured
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      color: '#212b36'
                    }}
                  >
                    Add your first slab to begin.
                  </Typography>
                  {!readOnly && (
                    <Button
                      className="button-variant-contained"
                      startIcon={<Iconify icon="eva:plus-fill" width={20} />}
                      onClick={handleAddSlab}
                    >
                      Create First Slab
                    </Button>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {(formData.slabs || []).map((slab, displayIndex) => {
                    const slabIndex = displayIndex;
                    return (
                      <Card
                        key={slab.id}
                        sx={{
                          border: '1px solid rgba(24, 25, 26, 0.29)',
                          borderRadius: 1.5,
                          boxShadow: 'none',
                          overflow: 'visible'
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: 'white',
                            borderBottom: '1px solid rgba(24, 25, 26, 0.29)',
                            py: 0.5,
                            px: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: '#212B36',
                                  fontSize: '16px',
                                }}
                              >
                                {`Slab ${displayIndex + 1}`}
                              </Typography>
                            </Box>
                            {/* <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ml: 5 }}>
                            {slab.boxMinQuantity && slab.boxMaxQuantity && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#637381',
                                  fontSize: '12px',
                                  bgcolor: '#F4F6F8',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 0.5
                                }}
                              >
                                📦 Box: {slab.boxMinQuantity}-{slab.boxMaxQuantity}
                              </Typography>
                            )}
                            {slab.pieceMinQuantity && slab.pieceMaxQuantity && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#637381',
                                  fontSize: '12px',
                                  bgcolor: '#F4F6F8',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 0.5
                                }}
                              >
                                🔢 Piece: {slab.pieceMinQuantity}-{slab.pieceMaxQuantity}
                              </Typography>
                            )}
                            {slab.discountType && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#637381',
                                  fontSize: '12px',
                                  bgcolor: '#F4F6F8',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 0.5
                                }}
                              >
                                💰 {slab.discountType === 'set-value' ? 'Set Value' : slab.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                              </Typography>
                            )}
                            {slab.hasGetProducts && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#10b981',
                                  fontSize: '12px',
                                  bgcolor: 'rgba(16, 185, 129, 0.08)',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 0.5,
                                  fontWeight: 600
                                }}
                              >
                                🎁 Get Products ({(slab.getProducts || []).length})
                              </Typography>
                            )}
                          </Box> */}
                          </Box>
                          {!readOnly && (formData.slabs || []).length > 1 && (
                            <IconButton
                              onClick={() => handleDeleteSlab(slabIndex)}
                              sx={{
                                color: '#637381',
                                '&:hover': {
                                  color: '#FF5630',
                                  bgcolor: 'rgba(255, 86, 48, 0.08)',
                                }
                              }}
                            >
                              <Iconify icon="eva:trash-2-outline" width={20} />
                            </IconButton>
                          )}
                        </Box>

                        <CardContent sx={{ p: 2 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 0.5,
                                  fontWeight: 600,
                                  color: '#212B36',
                                  fontSize: '13px'
                                }}
                              >
                                Slab Name <span style={{ color: '#FF5630' }}>*</span>
                              </Typography>
                              <TextField
                                className="input-border"
                                fullWidth
                                size="small"

                                value={slab.slabName || ''}
                                onChange={(e) =>
                                  handleSlabChange(slabIndex, 'slabName', e.target.value)
                                }
                                error={!!errors[`slab_${slabIndex}_name`]}
                                helperText={errors[`slab_${slabIndex}_name`]}
                                placeholder={`Enter name for Slab ${displayIndex + 1}`}
                                InputProps={{ readOnly: readOnly }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '14px',

                                    '&:hover fieldset': {
                                      borderColor: '#003DA5',
                                    },
                                  },
                                }}
                              />
                            </Grid>

                            {(formData.buyProductType === 'box-only' || formData.buyProductType === 'both') && (
                              <>
                                <Grid item xs={12} md={(formData.buyProductType === 'both') ? 4 : 4}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      mb: 0.5,
                                      fontWeight: 600,
                                      color: '#212B36',
                                      fontSize: '13px'
                                    }}
                                  >
                                    Box Quantity <span style={{ color: '#FF5630' }}>*</span>
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <TextField
                                      className="input-border"
                                      fullWidth
                                      size="small"
                                      placeholder="0"
                                      type="number"
                                      value={slab.boxMinQuantity || slab.boxMaxQuantity || ''}
                                      onChange={(e) =>
                                        handleQuantityChange(slabIndex, 'boxQuantity', e.target.value, 'Box Quantity')
                                      }
                                      onKeyDown={(e) => handleQuantityKeyDown(e, (slab.boxMaxQuantity || slab.boxMinQuantity || ''))}
                                      onWheel={(e) => e.target.blur()}
                                      inputProps={{ min: 0, max: 999 }}
                                      InputProps={{ readOnly: readOnly }}
                                      error={!!errors[`slab_${slabIndex}_boxRange`]}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          fontSize: '14px',

                                          '&:hover fieldset': {
                                            borderColor: '#003DA5',
                                          },
                                        },
                                      }}
                                    />
                                  </Box>
                                  {errors[`slab_${slabIndex}_boxRange`] && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#FF5630',
                                        display: 'block',
                                        mt: 0.5,
                                        fontSize: '12px'
                                      }}
                                    >
                                      {errors[`slab_${slabIndex}_boxRange`]}
                                    </Typography>
                                  )}
                                </Grid>
                              </>
                            )}

                            {(formData.buyProductType === 'piece-only' || formData.buyProductType === 'both') && (
                              <Grid item xs={12} md={(formData.buyProductType === 'both') ? 4 : 4}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mb: 0.5,
                                    fontWeight: 600,
                                    color: '#212B36',
                                    fontSize: '13px'
                                  }}
                                >
                                  Piece Quantity <span style={{ color: '#FF5630' }}>*</span>
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <TextField
                                    className="input-border"
                                    fullWidth
                                    size="small"
                                    placeholder="0"
                                    type="number"
                                    value={slab.pieceMinQuantity || slab.pieceMaxQuantity || ''}
                                    onChange={(e) =>
                                      handleQuantityChange(slabIndex, 'pieceQuantity', e.target.value, 'Piece Quantity')
                                    }
                                    onKeyDown={(e) => handleQuantityKeyDown(e, (slab.pieceMaxQuantity || slab.pieceMinQuantity || ''))}
                                    onWheel={(e) => e.target.blur()}
                                    inputProps={{ min: 0, max: 999 }}
                                    InputProps={{ readOnly: readOnly }}
                                    error={!!errors[`slab_${slabIndex}_pieceRange`]}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        fontSize: '14px',

                                        '&:hover fieldset': {
                                          borderColor: '#003DA5',
                                        },
                                      },
                                    }}
                                  />
                                </Box>
                                {formData.buyProductType === 'both' && getPackSize() > 0 && slab.boxMaxQuantity && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#637381',
                                      display: 'flex',
                                      mt: 0.5,
                                      ml: 1.75,
                                      justifyContent: 'flex-end',
                                      fontSize: '12px'
                                    }}
                                  >
                                    Maximum: {((parseInt(slab.boxMaxQuantity) || 0) + 1) * getPackSize() - 1} pieces
                                  </Typography>
                                )}
                                {errors[`slab_${slabIndex}_pieceRange`] && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#FF5630',
                                      display: 'block',
                                      mt: 0.5,
                                      fontSize: '12px'
                                    }}
                                  >
                                    {errors[`slab_${slabIndex}_pieceRange`]}
                                  </Typography>
                                )}
                              </Grid>
                            )}

                            {errors[`slab_${slabIndex}_range`] && (
                              <Grid item xs={12}>
                                <Alert
                                  severity="error"
                                  sx={{
                                    borderRadius: 1,
                                    '& .MuiAlert-icon': {
                                      color: '#FF5630'
                                    }
                                  }}
                                >
                                  {errors[`slab_${slabIndex}_range`]}
                                </Alert>
                              </Grid>
                            )}

                            {errors[`slab_${slabIndex}_conflict`] && (
                              <Grid item xs={12}>
                                <Alert
                                  severity="warning"
                                  sx={{
                                    borderRadius: 1,
                                    '& .MuiAlert-icon': {
                                      color: '#FF9800'
                                    }
                                  }}
                                >
                                  {errors[`slab_${slabIndex}_conflict`]}
                                </Alert>
                              </Grid>
                            )}

                            <Grid item xs={12} md={4}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 0.5,
                                  fontWeight: 600,
                                  color: '#212B36',
                                  fontSize: '13px'
                                }}
                              >
                                Discount Type <span style={{ color: '#FF5630' }}>*</span>
                              </Typography>
                              <FormControl fullWidth size="small" error={!!errors[`slab_${slabIndex}_discountType`]} sx={{

                              }}>
                                <Select
                                  className="input-border"
                                  size="small"

                                  value={slab.discountType || ''}
                                  displayEmpty
                                  onChange={(e) =>
                                    handleSlabChange(slabIndex, 'discountType', e.target.value)
                                  }
                                  disabled={readOnly}
                                  sx={{
                                    height: '35px',
                                    '& .MuiSelect-select': {
                                      display: 'flex',
                                      alignItems: 'center',
                                      minHeight: '35px !important',
                                    },
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    <Typography sx={{ color: '#212b36', fontSize: '14px' }}>
                                      Select discount type
                                    </Typography>
                                  </MenuItem>
                                  <MenuItem value="percentage" sx={{ fontSize: '14px' }}>
                                    Value Off - Percentage
                                  </MenuItem>
                                  <MenuItem value="fixed" sx={{ fontSize: '14px' }}>
                                    Value Off - Fixed Amount
                                  </MenuItem>
                                  {/* <MenuItem value="set-value" sx={{ fontSize: '14px' }}>
                                    Set Value
                                  </MenuItem> */}
                                </Select>
                                {errors[`slab_${slabIndex}_discountType`] && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#FF5630',
                                      display: 'block',
                                      mt: 0.5,
                                      fontSize: '12px'
                                    }}
                                  >
                                    {errors[`slab_${slabIndex}_discountType`]}
                                  </Typography>
                                )}
                              </FormControl>
                            </Grid>

                            <Grid item xs={8}>
                              {/* Left pair: Box Value + Final Box Price */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', gap: 1 }}>
                                {/* Box Products Value */}
                                {(formData.buyProductType === 'box-only' || formData.buyProductType === 'both')
                                  ? (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#212B36', fontSize: '13px' }}>
                                        {slab.discountType === 'percentage' ? 'Box Products Value' : 'Box Products Value (£)'} <span style={{ color: '#FF5630' }}>*</span>
                                      </Typography>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={slab.boxProductsValue || ''}
                                        onChange={(e) => {
                                          let raw = e.target.value;
                                          if (slab.discountType === 'percentage') {
                                            const num = parseFloat(raw);
                                            if (!Number.isNaN(num) && num > 100) {
                                              handleSlabChange(slabIndex, 'boxProductsValue', '100');
                                              return;
                                            }
                                            if (raw === '' || raw === '.') {
                                              handleSlabChange(slabIndex, 'boxProductsValue', raw);
                                              return;
                                            }
                                          }
                                          const num = parseFloat(raw);
                                          if (slab.discountType === 'fixed' && !Number.isNaN(num)) {
                                            const maxDiscount = getBaseBoxPrice();
                                            if (maxDiscount > 0 && num > maxDiscount) {
                                              return;
                                            }
                                          }
                                          const sanitized = (slab.discountType === 'percentage')
                                            ? (raw.includes('.') ? raw.split('.')[0] : raw)
                                            : (typeof limitDecimals === 'function' ? limitDecimals(raw, 2) : (() => {
                                              const s = String(raw).replace(/[^\d.]/g, '');
                                              const parts = s.split('.'); const ip = parts[0] || '0'; if (parts.length === 1) return ip;
                                              const fr = (parts[1] || '').slice(0, 2); return fr.length ? `${ip}.${fr}` : ip;
                                            })());
                                          if (slab.discountType === 'percentage') {
                                            const sanitizedNum = parseFloat(sanitized);
                                            if (!Number.isNaN(sanitizedNum) && sanitizedNum > 100) {
                                              handleSlabChange(slabIndex, 'boxProductsValue', '100');
                                              return;
                                            }
                                          }
                                          handleSlabChange(slabIndex, 'boxProductsValue', sanitized);
                                        }}
                                        onBlur={(e) => {
                                          const num = parseFloat(e.target.value);
                                          if (slab.discountType === 'percentage') {
                                            if (!Number.isNaN(num) && num > 100) {
                                              handleSlabChange(slabIndex, 'boxProductsValue', '100');
                                            }
                                          } else if (slab.discountType === 'fixed' && !Number.isNaN(num)) {
                                            const maxDiscount = getBaseBoxPrice();
                                            if (maxDiscount > 0 && num > maxDiscount) {
                                              handleSlabChange(
                                                slabIndex,
                                                'boxProductsValue',
                                                maxDiscount.toFixed(2)
                                              );
                                            }
                                          }
                                        }}
                                        onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
                                        onWheel={(e) => e.target.blur()}
                                        inputProps={{
                                          min: 0,
                                          max:
                                            slab.discountType === 'percentage'
                                              ? 100
                                              : (() => {
                                                const maxDiscount = getBaseBoxPrice();
                                                return maxDiscount > 0 ? maxDiscount : 999999;
                                              })(),
                                          step: slab.discountType === 'percentage' ? 1 : 0.01,
                                        }}
                                        error={!!errors[`slab_${slabIndex}_boxValue`]}
                                        helperText={
                                          <Typography sx={{ color: errors[`slab_${slabIndex}_boxValue`] ? '#FF5630' : '#212b36', fontSize: '12px', pt: -1 }}>
                                            {errors[`slab_${slabIndex}_boxValue`] ||
                                              (slab.discountType === 'percentage'
                                                ? 'Maximum 100%'
                                                : (() => {
                                                  const maxDiscount = getBaseBoxPrice();
                                                  return maxDiscount > 0
                                                ? `Maximum discount £${maxDiscount.toFixed(2)} (lowest product price)`
                                                    : 'Fixed amount off per box';
                                                })())}
                                          </Typography>
                                        }
                                        InputProps={slab.discountType === 'percentage' ? { readOnly: readOnly } : {
                                          readOnly: readOnly,
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <Typography sx={{ color: '#212b36', fontSize: '14px' }}>£</Typography>
                                            </InputAdornment>
                                          ),
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', '& fieldset': { borderColor: '#000' }, '&:hover fieldset': { borderColor: '#003DA5' } } }}
                                      />
                                    </Box>
                                  ) : <Box />}
                                {/* Final Box Price */}
                                {(formData.buyProductType === 'box-only' || formData.buyProductType === 'both')
                                  ? (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#212B36', fontSize: '13px' }}>
                                        Final Box Price
                                      </Typography>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        value={computeFinalBoxPrice(slab).toFixed(2)}
                                        disabled
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <Typography sx={{ color: '#212b36', fontSize: '14px' }}>£</Typography>
                                            </InputAdornment>
                                          ),
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', '& fieldset': { borderColor: '#000' }, '&:hover fieldset': { borderColor: '#003DA5' } } }}
                                      />
                                    </Box>
                                  ) : <Box />}
                              </Box>
                              {/* Right pair: Piece Value + Final Piece Price */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', gap: 1 }}>
                                {(formData.buyProductType === 'piece-only' || formData.buyProductType === 'both')
                                  ? (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#212B36', fontSize: '13px' }}>
                                        {slab.discountType === 'percentage' ? 'Piece Products Value' : 'Piece Products Value (£)'} <span style={{ color: '#FF5630' }}>*</span>
                                      </Typography>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={slab.pieceProductsValue || ''}
                                        onChange={(e) => {
                                          let raw = e.target.value;
                                          if (slab.discountType === 'percentage') {
                                            const num = parseFloat(raw);
                                            if (!Number.isNaN(num) && num > 100) {
                                              handleSlabChange(slabIndex, 'pieceProductsValue', '100');
                                              return;
                                            }
                                            if (raw === '' || raw === '.') {
                                              handleSlabChange(slabIndex, 'pieceProductsValue', raw);
                                              return;
                                            }
                                          }
                                          const num = parseFloat(raw);
                                          if (slab.discountType === 'fixed' && !Number.isNaN(num)) {
                                            const maxDiscount = getBasePiecePrice();
                                            if (maxDiscount > 0 && num > maxDiscount) {
                                              return;
                                            }
                                          }
                                          const sanitized = (slab.discountType === 'percentage')
                                            ? (raw.includes('.') ? raw.split('.')[0] : raw)
                                            : (typeof limitDecimals === 'function' ? limitDecimals(raw, 2) : (() => {
                                              const s = String(raw).replace(/[^\d.]/g, '');
                                              const parts = s.split('.'); const ip = parts[0] || '0'; if (parts.length === 1) return ip;
                                              const fr = (parts[1] || '').slice(0, 2); return fr.length ? `${ip}.${fr}` : ip;
                                            })());
                                          if (slab.discountType === 'percentage') {
                                            const sanitizedNum = parseFloat(sanitized);
                                            if (!Number.isNaN(sanitizedNum) && sanitizedNum > 100) {
                                              handleSlabChange(slabIndex, 'pieceProductsValue', '100');
                                              return;
                                            }
                                          }
                                          handleSlabChange(slabIndex, 'pieceProductsValue', sanitized);
                                        }}
                                        onBlur={(e) => {
                                          const num = parseFloat(e.target.value);
                                          if (slab.discountType === 'percentage') {
                                            if (!Number.isNaN(num) && num > 100) {
                                              handleSlabChange(slabIndex, 'pieceProductsValue', '100');
                                            }
                                          } else if (slab.discountType === 'fixed' && !Number.isNaN(num)) {
                                            const maxDiscount = getBasePiecePrice();
                                            if (maxDiscount > 0 && num > maxDiscount) {
                                              handleSlabChange(
                                                slabIndex,
                                                'pieceProductsValue',
                                                maxDiscount.toFixed(2)
                                              );
                                            }
                                          }
                                        }}
                                        onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
                                        onWheel={(e) => e.target.blur()}
                                        inputProps={{
                                          min: 0,
                                          max:
                                            slab.discountType === 'percentage'
                                              ? 100
                                              : (() => {
                                                const maxDiscount = getBasePiecePrice();
                                                return maxDiscount > 0 ? maxDiscount : 999999;
                                              })(),
                                          step: slab.discountType === 'percentage' ? 1 : 0.01,
                                        }}
                                        error={!!errors[`slab_${slabIndex}_pieceValue`]}
                                        helperText={
                                          errors[`slab_${slabIndex}_pieceValue`] ||
                                          (slab.discountType === 'percentage'
                                            ? 'Maximum 100%'
                                            : (() => {
                                              const maxDiscount = getBasePiecePrice();
                                              return maxDiscount > 0
                                                ? `Maximum discount £${maxDiscount.toFixed(2)} (lowest product price)`
                                                : 'Fixed amount off per piece';
                                            })())
                                        }
                                        InputProps={slab.discountType === 'percentage' ? { readOnly: readOnly } : {
                                          readOnly: readOnly,
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <Typography sx={{ color: '#212b36', fontSize: '14px' }}>£</Typography>
                                            </InputAdornment>
                                          ),
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', '& fieldset': { borderColor: '#000' }, '&:hover fieldset': { borderColor: '#003DA5' } } }}
                                      />
                                    </Box>
                                  ) : <Box />}
                                {(formData.buyProductType === 'piece-only' || formData.buyProductType === 'both')
                                  ? (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#212B36', fontSize: '13px' }}>
                                        Final Piece Price
                                      </Typography>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        value={computeFinalPiecePrice(slab).toFixed(2)}
                                        disabled
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <Typography sx={{ color: '#212b36', fontSize: '14px' }}>£</Typography>
                                            </InputAdornment>
                                          ),
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', '& fieldset': { borderColor: '#000' }, '&:hover fieldset': { borderColor: '#003DA5' } } }}
                                      />
                                    </Box>
                                  ) : <Box />}
                              </Box>
                            </Grid>

                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={slab.hasGetProducts || false}
                                    onChange={
                                      readOnly
                                        ? undefined
                                        : (e) => handleSlabChange(slabIndex, 'hasGetProducts', e.target.checked)
                                    }
                                    disabled={readOnly}
                                    sx={{
                                      color: '#F8991D',
                                      '&.Mui-checked': {
                                        color: '#F8991D',
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '14px'
                                      }}
                                    >
                                      Include Get Products (Free Items)
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#212B36',
                                        fontSize: '11px',
                                        display: 'block',
                                        fontStyle: 'italic'
                                      }}
                                    >
                                      {slab.hasGetProducts
                                        ? 'Get products are applicable to this slab'
                                        : 'Only discounts on buy products will apply (no get products)'}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Grid>
                          </Grid>

                          {slab.hasGetProducts && (
                            <Box>
                              <Divider sx={{ mb: 2.5 }} />
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  gap: 2,
                                  mb: 2,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    color: '#212B36',
                                    fontSize: '15px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Get Products ({(slab.getProducts || []).length})
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: '#212B36',
                                      fontSize: '13px',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    Total Get Quantity <span style={{ color: 'red' }}>*</span>
                                  </Typography>
                                  <TextField
                                    size="small"
                                    className="input-border"
                                    value={slab.getQuantity ?? ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 9999)) {
                                        handleSlabChange(slabIndex, 'getQuantity', value);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    placeholder="Qty"
                                    type="number"
                                    inputProps={{ min: 1, max: 9999 }}
                                    InputProps={{ readOnly: readOnly }}
                                    onWheel={(e) => e.target.blur()}
                                    error={!!errors[`slab_${slabIndex}_getQuantity`]}
                                    sx={{
                                      width: 100,
                                      '& input[type=number]': { MozAppearance: 'textfield' },
                                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                      },
                                    }}
                                  />
                                </Box>
                                {!readOnly && (
                                  <Button
                                    variant="outlined"
                                    size="large"
                                    color="error"
                                    startIcon={<Iconify icon="eva:plus-fill" width={18} />}
                                    onClick={() => handleAddGetProducts(slabIndex)}
                                    sx={{
                                      flex: '0 0 auto',
                                      outline: 'none',
                                      whiteSpace: 'nowrap',
                                      '&:focus, &:focus-visible, &:active, &:focus-within': {
                                        outline: 'none',
                                        boxShadow: '0 0 0 2px rgba(255, 86, 48, 0.3)',
                                      },
                                      '& .MuiTouchRipple-root': {
                                        display: 'none',
                                      },
                                    }}
                                    disableRipple
                                  >
                                    Add More Products
                                  </Button>
                                )}
                              </Box>
                              {errors[`slab_${slabIndex}_getProducts`] && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#FF5630',
                                    display: 'block',
                                    mt: -1,
                                    mb: 2,
                                    fontSize: '12px'
                                  }}
                                >
                                  {errors[`slab_${slabIndex}_getProducts`]}
                                </Typography>
                              )}
                              {errors[`slab_${slabIndex}_getQuantity`] && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#FF5630',
                                    display: 'block',
                                    mt: -1,
                                    mb: 2,
                                    fontSize: '12px'
                                  }}
                                >
                                  {errors[`slab_${slabIndex}_getQuantity`]}
                                </Typography>
                              )}

                              {(slab.getProducts || []).length === 0 ? (
                                <Box
                                  sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    bgcolor: '#F9FAFB',
                                    border: '1px dashed rgba(59, 59, 59, 0.45)',
                                    borderRadius: 1.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#212b36' }}
                                  >
                                    No get products added yet
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {(slab.getProducts || []).map((product, pIndex) => {
                                    const buyProductType = formData.buyProductType || 'box-only';
                                    let defaultTypeForGet = 'Box';
                                    if (buyProductType === 'box-only') {
                                      defaultTypeForGet = 'Box';
                                    } else if (buyProductType === 'piece-only') {
                                      defaultTypeForGet = 'Piece';
                                    } else {
                                      defaultTypeForGet = 'Box';
                                    }

                                    const normalizedProduct = normalizeProductPricing({
                                      ...product,
                                      type: normalizeProductType(product.type, product.split)
                                    });

                                    const buyProductTypeForPrice = formData.buyProductType || 'box-only';
                                    let defaultTypeForPrice = 'Box';
                                    if (buyProductTypeForPrice === 'box-only') {
                                      defaultTypeForPrice = 'Box';
                                    } else if (buyProductTypeForPrice === 'piece-only') {
                                      defaultTypeForPrice = 'Piece';
                                    } else {
                                      defaultTypeForPrice = 'Box';
                                    }

                                    const effectiveType =
                                      (buyProductTypeForPrice === 'both' && normalizedProduct.type && normalizedProduct.type !== 'Both' && normalizedProduct.type !== 'undefined' && normalizedProduct.type !== 'null')
                                        ? normalizedProduct.type
                                        : defaultTypeForPrice;

                                    // Display price according to the selected product type
                                    const effectiveSalesPrice =
                                      effectiveType === 'Piece'
                                        ? (normalizedProduct.pieceSalesPrice || 0)
                                        : (normalizedProduct.boxSalesPrice || normalizedProduct.salesPrice || 0);
                                    const effectiveOfferPrice =
                                      effectiveType === 'Piece'
                                        ? (normalizedProduct.pieceOfferPrice || 0)
                                        : (normalizedProduct.boxOfferPrice || normalizedProduct.offerPrice || 0);
                                    return (
                                      <Card
                                        key={`${product.id}-${pIndex}`}
                                        sx={{
                                          border: '1px solid rgba(24, 25, 26, 0.29)',
                                          boxShadow: 'none',
                                          borderRadius: 1.5,
                                          '&:hover': {
                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                          }
                                        }}
                                      >
                                        <CardContent sx={{ px: 2, py: 1, '&:last-child': { pb: 1 } }}>
                                          <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: '#212B36',
                                                  mb: 0.5,
                                                  fontSize: '14px'
                                                }}
                                              >
                                                {normalizedProduct.name}
                                              </Typography>
                                              <Typography
                                                key={`stock-${normalizedProduct.id}-${pIndex}-${effectiveType}`}
                                                variant="body2"
                                                sx={{
                                                  color: '#212B36',
                                                  mb: 1.5,
                                                  fontSize: '12px'
                                                }}
                                              >
                                                Code: {normalizedProduct.code || normalizedProduct.id} | Stock: {getStockByType(normalizedProduct)}
                                              </Typography>

                                              <Grid container spacing={2}>
                                                <Grid item xs={12} sm={normalizedProduct.compulsory ? 3 : 4}>
                                                  {(() => {
                                                    const buyProductType = formData.buyProductType || 'box-only';
                                                    let defaultType = 'Box';
                                                    let isTypeDisabled = false;

                                                    if (buyProductType === 'box-only') {
                                                      defaultType = 'Box';
                                                      isTypeDisabled = true;
                                                    } else if (buyProductType === 'piece-only') {
                                                      defaultType = 'Piece';
                                                      isTypeDisabled = true;
                                                    } else if (buyProductType === 'both') {
                                                      defaultType = 'Box'; // Always default to Box for 'both'
                                                      isTypeDisabled = true; // Readonly when 'both' is selected
                                                    }

                                                    // For 'both', use the product's type if it exists and is valid, otherwise use defaultType (Box)
                                                    // For 'box-only' or 'piece-only', always use defaultType (disabled)
                                                    const finalType = (buyProductType === 'both' && normalizedProduct.type && normalizedProduct.type !== 'Both' && normalizedProduct.type !== 'undefined' && normalizedProduct.type !== 'null')
                                                      ? normalizedProduct.type
                                                      : defaultType;

                                                    return (
                                                      <FormControl fullWidth size="small">
                                                        <InputLabel sx={{ fontSize: '13px' }}>Product Type</InputLabel>
                                                        <Select
                                                          className="input-border"
                                                          value={finalType}
                                                          label="Product Type"
                                                          onChange={(e) => {
                                                            if (!isTypeDisabled && handleSlabProductTypeChange) {
                                                              handleSlabProductTypeChange(
                                                                slabIndex,
                                                                `${product.id}-${pIndex}`,
                                                                e.target.value
                                                              );
                                                            }
                                                          }}
                                                          disabled={readOnly || isTypeDisabled}
                                                          sx={{
                                                            fontSize: '13px',

                                                          }}
                                                          MenuProps={buildMenuProps({
                                                            PaperProps: {
                                                              sx: {
                                                                '& .MuiMenuItem-root': {
                                                                  fontSize: '13px',
                                                                }
                                                              }
                                                            }
                                                          })}
                                                        >
                                                          {(() => {
                                                            const availableOptions = getProductTypeOptions(normalizedProduct.split || 2, false);
                                                            if (isTypeDisabled) {
                                                              return availableOptions.filter(opt => opt.value === defaultType).map((option) => (
                                                                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '13px' }}>
                                                                  {option.label}
                                                                </MenuItem>
                                                              ));
                                                            }
                                                            return availableOptions.map((option) => (
                                                              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '13px' }}>
                                                                {option.label}
                                                              </MenuItem>
                                                            ));
                                                          })()}
                                                        </Select>
                                                      </FormControl>
                                                    );
                                                  })()}
                                                </Grid>

                                                <Grid item xs={12} sm={normalizedProduct.compulsory ? 3 : 4}>
                                                  {(() => {
                                                    const buyProductTypeForPricing = formData.buyProductType || 'box-only';
                                                    let defaultTypeForPricing = 'Box';
                                                    let isTypeDisabledForPricing = false;

                                                    if (buyProductTypeForPricing === 'box-only') {
                                                      defaultTypeForPricing = 'Box';
                                                      isTypeDisabledForPricing = true;
                                                    } else if (buyProductTypeForPricing === 'piece-only') {
                                                      defaultTypeForPricing = 'Piece';
                                                      isTypeDisabledForPricing = true;
                                                    } else if (buyProductTypeForPricing === 'both') {
                                                      defaultTypeForPricing = 'Box';
                                                      isTypeDisabledForPricing = false;
                                                    }

                                                    const currentTypeForPricing = (buyProductTypeForPricing === 'both' && normalizedProduct.type && normalizedProduct.type !== 'Both' && normalizedProduct.type !== 'undefined' && normalizedProduct.type !== 'null')
                                                      ? normalizedProduct.type
                                                      : defaultTypeForPricing;

                                                    const currentSalesPrice = currentTypeForPricing === 'Piece'
                                                      ? (normalizedProduct.pieceSalesPrice || 0)
                                                      : (normalizedProduct.boxSalesPrice || normalizedProduct.salesPrice || 0);
                                                    const currentOfferPrice = currentTypeForPricing === 'Piece'
                                                      ? (normalizedProduct.pieceOfferPrice || 0)
                                                      : (normalizedProduct.boxOfferPrice || normalizedProduct.offerPrice || 0);

                                                    const selectedPricingType = getMultiLayerGetPricingMode(
                                                      normalizedProduct,
                                                      currentTypeForPricing,
                                                      fromApiPromotionPricingType(formData.getPricingType, 'sales')
                                                    );

                                                    return (
                                                      <FormControl fullWidth size="small">
                                                        <InputLabel sx={{ fontSize: '13px' }}>Pricing</InputLabel>
                                                        <Select
                                                          className="input-border"
                                                          value={selectedPricingType}
                                                          label="Pricing"
                                                          onChange={(e) => {
                                                            if (handleSlabProductPricingChange) {
                                                              handleSlabProductPricingChange(
                                                                slabIndex,
                                                                `${product.id}-${pIndex}`,
                                                                'pricingType',
                                                                e.target.value
                                                              );
                                                            }
                                                          }}
                                                          disabled={readOnly}
                                                          renderValue={(selectedValue) => {
                                                            const actualPricingType =
                                                              getMultiLayerGetPricingMode(
                                                                normalizedProduct,
                                                                currentTypeForPricing,
                                                                fromApiPromotionPricingType(
                                                                  formData.getPricingType,
                                                                  'sales'
                                                                )
                                                              ) || selectedValue || 'sales';
                                                            return actualPricingType === 'offer' ? 'Offer' : 'Standard';
                                                          }}
                                                          sx={{
                                                            fontSize: '13px',
                                                            // '& .MuiOutlinedInput-notchedOutline': {
                                                            //   borderColor: '#DFE3E8',
                                                            // }
                                                          }}
                                                          MenuProps={buildMenuProps({
                                                            PaperProps: {
                                                              sx: {
                                                                '& .MuiMenuItem-root': {
                                                                  fontSize: '13px',
                                                                }
                                                              }
                                                            }
                                                          })}
                                                        >
                                                          <MenuItem value="sales" sx={{ fontSize: '13px' }}>
                                                            Standard
                                                          </MenuItem>
                                                          <MenuItem value="offer" sx={{ fontSize: '13px' }}>
                                                            Offer
                                                          </MenuItem>
                                                        </Select>
                                                      </FormControl>
                                                    );
                                                  })()}
                                                </Grid>

                                                <Grid item xs={12} sm={normalizedProduct.compulsory ? 3 : 4}>
                                                  <FormControlLabel
                                                    control={
                                                      <Checkbox
                                                        checked={normalizedProduct.compulsory || false}
                                                        onChange={
                                                          readOnly
                                                            ? undefined
                                                            : (e) =>
                                                              handleSlabProductCompulsoryChange &&
                                                              handleSlabProductCompulsoryChange(
                                                                slabIndex,
                                                                `${product.id}-${pIndex}`,
                                                                e.target.checked
                                                              )
                                                        }
                                                        disabled={readOnly}
                                                        sx={{
                                                          color: '#F8991D',
                                                          '&.Mui-checked': {
                                                            color: '#F8991D',
                                                          },
                                                        }}
                                                      />
                                                    }
                                                    label="Compulsory"
                                                  />
                                                </Grid>
                                                {normalizedProduct.compulsory && (
                                                  <Grid item xs={12} sm={2}>
                                                    <TextField
                                                      fullWidth
                                                      size="small"
                                                      className="input-border"
                                                      label={
                                                        <span>
                                                          Qty <span style={{ color: 'red' }}>*</span>
                                                        </span>
                                                      }
                                                      value={normalizedProduct.quantity ?? ''}
                                                      onChange={(e) => {
                                                        let value = e.target.value;
                                                        if (
                                                          value === '' ||
                                                          (/^\d+$/.test(value) && parseInt(value) <= 9999)
                                                        ) {
                                                          const getQty = parseInt(slab.getQuantity, 10);
                                                          if (
                                                            value !== '' &&
                                                            !isNaN(getQty) &&
                                                            getQty > 0 &&
                                                            parseInt(value, 10) > getQty
                                                          ) {
                                                            value = String(getQty);
                                                            setSnackbar({
                                                              open: true,
                                                              message: `Compulsory quantity cannot exceed get quantity (${getQty})`,
                                                            });
                                                          }
                                                          handleSlabProductQuantityChange &&
                                                          handleSlabProductQuantityChange(
                                                            slabIndex,
                                                            `${product.id}-${pIndex}`,
                                                            value
                                                          );
                                                        }
                                                      }}
                                                      onKeyDown={(e) => {
                                                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                                          e.preventDefault();
                                                        }
                                                      }}
                                                      type="number"
                                                      inputProps={{
                                                        min: 1,
                                                        max: (() => {
                                                          const getQty = parseInt(slab.getQuantity, 10);
                                                          return !isNaN(getQty) && getQty > 0 ? getQty : 9999;
                                                        })(),
                                                      }}
                                                      InputProps={{ readOnly: readOnly }}
                                                      onWheel={(e) => e.target.blur()}
                                                      error={!!errors[`slab_${slabIndex}_getProduct_${pIndex}_quantity`]}
                                                      helperText={errors[`slab_${slabIndex}_getProduct_${pIndex}_quantity`]}
                                                      sx={{
                                                        '& input[type=number]': { MozAppearance: 'textfield' },
                                                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                                          WebkitAppearance: 'none',
                                                          margin: 0,
                                                        },
                                                      }}
                                                    />
                                                  </Grid>
                                                )}
                                              </Grid>
                                            </Box>

                                            {!readOnly && (
                                              <IconButton
                                                onClick={() =>
                                                  handleSlabRemoveProduct &&
                                                  handleSlabRemoveProduct(slabIndex, `${product.id}-${pIndex}`)
                                                }
                                                sx={{
                                                  width: 28,
                                                  height: 28,
                                                  color: '#FF5630',
                                                  '&:hover': {
                                                    bgcolor: 'rgba(255, 86, 48, 0.08)',
                                                  }
                                                }}
                                              >
                                                <Iconify icon="eva:close-circle-fill" width={20} />
                                              </IconButton>
                                            )}
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                  {!readOnly && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        className="button-variant-outlined"

                        size="medium"
                        startIcon={<Iconify icon="eva:plus-fill" width={20} />}
                        onClick={handleAddSlab}
                      >
                        Add New Slab
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: '' })}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
